import { Background, BackgroundVariant, ReactFlow, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useStore } from '../../../app/data/root.store';
import { GraphContainer } from '../../EntitiesPage.styles';
import { CustomEdge } from '../edges/CustomEdge';
import CustomEntityNode from '../nodes/CustomEntityNode';
import { GhostEntityNode } from '../nodes/GhostEntityNode';
import { useGhostNodes } from './useGhostNodes';

const nodeTypes = {
    'custom-entity-node': CustomEntityNode,
    'custom': CustomEntityNode,
    'ghost-entity-node': GhostEntityNode
};

const edgeTypes = {
    'custom-entity-edge': CustomEdge,
    'default': CustomEdge
};

export const EntitiesChart = observer((props) => {
    const { 
        nodes,
        edges, 
        onNodesChange, 
        onEdgesChange, 
        handleNodeClick, 
        handleNodeMouseEnter, 
        handleNodeMouseLeave, 
        handleConnect, 
        handleEdgeClick, 
        isEmptyStateSingleEntity, 
        handleAddEntity, 
        onInit,
        children
    } = props;
    
    const { entitiesStore } = useStore();
    const { fitView } = useReactFlow();

    const ghostNodesAndEdges = useGhostNodes(isEmptyStateSingleEntity, nodes);
    
    const [isCreatingConnection, setIsCreatingConnection] = useState(false);

    useEffect(() => {
        const tempEdge = edges.find(edge => edge.id.startsWith('temp-'));
        if (tempEdge) {
            entitiesStore.setOpenPopoverId(tempEdge.id);
            setIsCreatingConnection(true);
            setTimeout(() => setIsCreatingConnection(false), 100);
        }
    }, [edges, entitiesStore]);

    useEffect(() => {
        let timeoutId;
        const handleResize = () => {
            timeoutId = setTimeout(() => {
                fitView({ duration: 300 });
            }, 100);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeoutId);
        };
    }, [fitView]);

    const handlePaneClick = useCallback(event => {
        if (isCreatingConnection) return;
        const target = event.target;
        if (!target.closest('.edge_edit_popover_wrapper,.edgeEditDrawerContainer_mobile')) {
            entitiesStore.closePopover();
            entitiesStore.setSelectedEntityId(null);
        }
    }, [entitiesStore, isCreatingConnection]);

    const handleMoveStart = useCallback((event) => {
        entitiesStore.closePopover();
    }, [entitiesStore]);

    const handleGhostNodeClick = useCallback((event, node) => {
        if (node.id === 'ghost-add-entity') {
            event.stopPropagation();
            handleAddEntity();
            return;
        }
        handleNodeClick?.(event, node);
    }, [handleAddEntity, handleNodeClick]);

    const allNodes = [...nodes, ...ghostNodesAndEdges.ghostNodes];
    const allEdges = [...edges, ...ghostNodesAndEdges.ghostEdges];

    const handleDelete = useCallback((event, node) => {
        entitiesStore.setShowDeleteModal(true);
    }, [entitiesStore]);

    const handleBeforeDelete = useCallback(({ nodes, edges }) => {
        return false;
    }, []);

    return (
        <GraphContainer>
            <ReactFlow
                nodes={allNodes}
                edges={allEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={handleGhostNodeClick}
                onNodeMouseEnter={handleNodeMouseEnter}
                onNodeMouseLeave={handleNodeMouseLeave}
                onConnect={handleConnect}
                onEdgeClick={handleEdgeClick}
                onPaneClick={handlePaneClick}
                onMoveStart={handleMoveStart}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                snapToGrid={true}
                snapGrid={[15, 15]}
                fitView
                attributionPosition="bottom-left"
                onInit={onInit}
                onDelete={handleDelete}
                onBeforeDelete={handleBeforeDelete}
            >
                <Background
                    variant={BackgroundVariant.Dots}
                    gap={12}
                    size={1}
                    color="#e5e7eb"
                />
                {children}
            </ReactFlow>
        </GraphContainer>
    );
});